import { Formik } from "formik";
import React, { useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { login, setNavigateToExpert, setNavigateToOrg } from "./authSlice";
import BlockUI from "../../base/components/BlockUI";
import { EyeInvisible, EyeVisible } from "../../assets/icons";
import { Checkbox, Spin } from "antd";
import https from "../../base/utils/http";
import AuthService from "../../base/services/authentication.service";
import { UserRolesEnum } from "./userRoles.enum";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "../../i18n/LanguageContext";
import ShowcaseCard from "./components/ShowcaseCard";
import LanguageSwitcher from "../../../_metronic/layout/components/subheader/components/LanguageSwitcher";
import "./custom.css";
import "./login.scss";
import { takeRedirectAfterLogin } from "./redirectAfterLogin";

const initialValues = {
   email: "",
   password: ""
};

function Login() {
   const { t } = useTranslation();
   const { isLoading } = useSelector((state) => state.auth);
   const navigateToOrg = useSelector((state) => state.auth.navigateToOrg);
   const navigateToExpert = useSelector((state) => state.auth.navigateToExpert);
   const dispatch = useDispatch();
   const [checked, setChecked] = useState(false);
   const { executeRecaptcha } = useGoogleReCaptcha();

   const [type, setType] = useState("password");
   const [icon, setIcon] = useState(EyeInvisible);

   const navigate = useNavigate();

   const toggleChecked = () => {
      setChecked(!checked);
   };

   const onChange = (e) => {
      setChecked(e.target.checked);
   };

   const LoginSchema = Yup.object().shape({
      email: Yup.string()
         .email(t("invalid-email-validation"))
         .required(t("required-validation")),
      password: Yup.string()
         .max(16, t("max-16-symbols-validation"))
         .required(t("required-validation"))
   });

   const handleToggle = () => {
      if (type === "password") {
         setIcon(EyeVisible);
         setType("text");
      } else {
         setIcon(EyeInvisible);
         setType("password");
      }
   };

   const submitForm = async (values) => {
      if(!executeRecaptcha) {
         return;
      }
      const recaptchaToken = await executeRecaptcha('login');
      dispatch(login(values, successCallback, errCB, recaptchaToken));
   };

   const successCallback = () => {
      // A link opened while logged out (see RedirectToLoginIfLoggedOut) wins over the start page.
      const redirectTarget = takeRedirectAfterLogin();
      if (redirectTarget) {
         navigate(redirectTarget, { replace: true });
         return;
      }
      if(AuthService.isAuthorized([UserRolesEnum.SuperAdmin])) {
         navigate("/admin/workspace");
      } else if(AuthService.isAuthorized([UserRolesEnum.Approver])) {
         navigate("/approver/submissions");
      } else {
         navigate("/workspace");
      }
   };

   const errCB = (data) => {
      if (data.target?.targetUrl) {
         navigate(data.target?.targetUrl, {
            state: { fromApp: true, data: data.target.params }
         });
      }
   };

   return (
      <div className="kap-login">
         {process.env.REACT_APP_BASE_URL === "https://stagingv2api.kaporg.com/api" && (
            <div style={{ position: "fixed", top: "20px", right: "20px", zIndex: 10 }}>
               <LanguageSwitcher color="#4158CF" />
            </div>
         )}
         <ShowcaseCard name="login" />
         <div className="kap-login__content">
            {isLoading ? (
               <BlockUI isLoading={isLoading} />
            ) : (
               <div className="kap-login__form">
                  <div className="d-flex flex-column" style={{ gap: "8px", marginBottom: "36px" }}>
                     <p className="kap-login__title">{t("welcome-back-title")}</p>
                     <p className="kap-login__subtitle">{t("login-into-account-subtitle")}</p>
                  </div>
                  <Formik
                     initialValues={initialValues}
                     validationSchema={LoginSchema}
                     onSubmit={(values) => {
                        submitForm(values);
                     }}
                  >
                     {({ handleSubmit, handleChange, values, errors }) => (
                        <form
                           onSubmit={handleSubmit}
                           className="form fv-plugins-bootstrap fv-plugins-framework"
                        >
                           <div className="kap-login__fields">
                              <div className="form-group fv-plugins-icon-container">
                                 <label className="kap-login__label">{t("email")}</label>
                                 <input
                                    placeholder={t("email-placeholder")}
                                    className="form-control"
                                    type="email"
                                    name="email"
                                    onChange={handleChange}
                                    values={values.email}
                                 />
                                 {errors.email && <div className="error-message">{errors.email}</div>}
                              </div>
                              <div className="form-group fv-plugins-icon-container">
                                 <label className="kap-login__label">{t("password-label")}</label>
                                 <div className="password-section">
                                    <input
                                       autoComplete="off"
                                       placeholder={t("password-label")}
                                       className="form-control"
                                       type={type}
                                       name="password"
                                       onChange={handleChange}
                                       values={values.password}
                                       maxLength={16}
                                    />
                                    <img
                                       className="eyeButton"
                                       onClick={handleToggle}
                                       src={icon}
                                       alt={t("toggle-password-visibility-label")}
                                       style={{ width: "14px" }}
                                    />
                                 </div>
                                 {errors.password && (
                                    <div className="error-message">{errors.password}</div>
                                 )}
                              </div>
                           </div>
                           <div className="kap-login__options">
                              <div className="d-flex align-items-center">
                                 <Checkbox
                                    checked={checked}
                                    onChange={onChange}
                                    style={{ lineHeight: "0px" }}
                                 />
                                 <p>{t("remember-me-label")}</p>
                              </div>
                              <Link
                                 to="/auth/forgot-password"
                                 className="password-text"
                                 id="kt_login_forgot"
                                 style={{ fontSize: "11px" }}
                              >
                                 {t("forgot-password-title")}
                              </Link>
                           </div>
                           <button
                              id="kt_login_signin_submit"
                              type="submit"
                              className="btn sign-btn font-weight-bold kap-login__submit"
                           >
                              {isLoading ? <Spin /> : t("log-in-button")}
                           </button>
                        </form>
                     )}
                  </Formik>
                  <p className="kap-login__signup">
                     {t("no-account-yet-text")}{" "}
                     <Link to="/auth/registration" className="ml-1 sign-up-btn" id="kt_login_signup">
                        {t("sign-up-link")}
                     </Link>
                  </p>
               </div>
            )}
         </div>
      </div>
   );
}

export default injectIntl(Login);
